import { create, type StoreApi, type UseBoundStore } from 'zustand'
import { persist } from 'zustand/middleware'
import { createDebouncedJSONStorage } from 'zustand-debounce'
import axios from 'axios'
import { getApiClient } from '~/stores'

type WebchatConfigState = {
  _hasHydrated: boolean
  _updating: boolean
  content: string
}

type WebchatConfigActions = {
  setContent: (content: string) => void
  saveConfig: () => Promise<void>
  save: (props: { index: boolean }) => Promise<void>
  setHasHydrated: (hasHydrated: boolean) => void
}

const storeInstances: Record<string, UseBoundStore<StoreApi<WebchatConfigActions & WebchatConfigState>>> = {}

const fileTags = {
  type: 'knowledge-base',
  kbId: 'kb-default',
  system: 'true',
  title: 'Rich Text Source',
  initialBotFile: 'true',
  dsType: 'rich-text',
}

export const getUseRichTextSourceStore = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  const client = getApiClient({ botId, workspaceId })
  if (!storeInstances[botId]) {
    storeInstances[botId] = create<WebchatConfigActions & WebchatConfigState>()(
      persist(
        (set, get) => ({
          content: '',
          _hasHydrated: false,
          _updating: false,
          setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
          setContent: (content) => set({ content }),
          saveConfig: async () => {},
          save: async ({ index }) => {
            const { content } = get()
            if (!content) {
              console.warn('No content to save for rich text source')
              return
            }

            set({ _updating: true })
            try {
              await client.uploadFile({
                key: `rich-text-source-${botId}`,
                content,
                index,
                contentType: 'text/html',
                tags: fileTags,
              })
            } catch (error) {
              console.error('Failed to save rich text source:', error)
            } finally {
              set({ _updating: false })
            }
          },
        }),
        {
          name: `rich-text-source-${botId}`,
          onRehydrateStorage: (state) => () => state.setHasHydrated(true),
          storage: createDebouncedJSONStorage(
            {
              getItem: async () => {
                // Implement your custom get logic here
                const files = await client.list
                  .files({
                    tags: fileTags,
                  })
                  .collect()

                const [richTextFile] = files.sort(
                  (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )

                if (!richTextFile) {
                  return '{}'
                }

                const { data } = await axios.get(richTextFile.url, {
                  withCredentials: false,
                })

                return JSON.stringify({
                  state: { content: data },
                })
              },
              setItem: (key, value) => {},
              removeItem: () => {
                // In our case we never want to delete the file, we can leave this empty
              },
            },
            { debounceTime: 3000 }
          ),
        }
      )
    )
  }
  return storeInstances[botId]
}
